/**
 * ProfileField — dispatcher component that renders the correct MUI input
 * for each field_type returned by the Facility Profile schema API.
 *
 * Supported field_type values (14 total):
 *   text | textarea | date (paste mm/dd/yyyy or yyyy-mm-dd; stored ISO) | number | currency | phone | email | year
 *   select | multiselect | radio | checkbox | checkbox_group | toggle
 *
 * Unknown types fall through to a plain TextField with a console warning,
 * ensuring forward-compatibility with new types added via seed data only.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { ProfileFieldOption, ProfileFieldResponse } from "@services/facilityManagement";
import { profileHelpTextForViewer } from "@utils/userUtils";
import {
  formatUsDateDraftInput,
  isoDateOnlyToUsSlash,
  normalizeProfileDateInputToIso,
  parseIsoDateOnlyAsLocalDate,
} from "@utils/formatUtils";

export interface ProfileFieldProps {
  field: ProfileFieldResponse;
  /** Raw value (what the UI holds, NOT the envelope). */
  value: unknown;
  onChange: (fieldKey: string, value: unknown) => void;
  disabled?: boolean;
  /** When true, renders the field with an error/invalid state (red outline). */
  hasError?: boolean;
}

const FIELD_SX = { fontFamily: "Rethink Sans, sans-serif" };
/** Labels wrap to multiple lines so long API copy stays readable in narrow cells. */
const LABEL_SX = {
  color: "#4b5563",
  fontWeight: 500,
  fontFamily: "Rethink Sans, sans-serif",
  whiteSpace: "normal",
  lineHeight: 1.35,
  wordBreak: "break-word",
} as const;
/** Vertical spacing between stacked fields; keep dense forms readable. */
const FIELD_BLOCK_SX = { mb: 1.75, width: "100%", minWidth: 0 };

/** Shared outlined input shell to match mockup polish (rounded, white fill). */
const OUTLINED_TEXTFIELD_SX = {
  fontFamily: "Rethink Sans, sans-serif",
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-input": {
    overflow: "visible",
    textOverflow: "clip",
  },
} as const;

/** Closed select shows full option text on multiple lines; menu items wrap for long labels. */
const SELECT_FIELD_SX = {
  ...FIELD_SX,
  borderRadius: 2,
  bgcolor: "#fff",
  "& .MuiSelect-select": {
    whiteSpace: "normal",
    lineHeight: 1.35,
    py: 1,
    display: "block",
  },
} as const;

const SELECT_MENU_PAPER_SX = {
  maxWidth: "min(100vw - 32px, 560px)",
};

const MENU_ITEM_WRAP_SX = {
  whiteSpace: "normal",
  lineHeight: 1.35,
  wordBreak: "break-word",
} as const;

const OPTION_LABEL_TYPOGRAPHY_SX = {
  ...FIELD_SX,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.35,
} as const;

const TEAL_SWITCH_SX = {
  flexShrink: 0,
  mt: "1px",
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#0e7490" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "rgba(14, 116, 144, 0.45)",
  },
} as const;

/** Row layout for profile toggles — switch track aligned with first label line. */
export const PROFILE_TOGGLE_ROW_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
} as const;

export const PROFILE_TOGGLE_TEXT_SX = {
  flex: 1,
  minWidth: 0,
  pt: "9px",
  ...LABEL_SX,
  fontSize: "0.875rem",
  lineHeight: 1.4,
} as const;

/** @deprecated Use PROFILE_TOGGLE_ROW_SX — kept for import compatibility. */
export const PROFILE_TOGGLE_LABEL_SX = PROFILE_TOGGLE_ROW_SX;

export const PROFILE_TEAL_SWITCH_SX = TEAL_SWITCH_SX;

function ToggleInlineLabel({
  id,
  label,
  isRequired,
  helpText,
}: {
  id?: string;
  label: string;
  isRequired?: boolean;
  helpText?: string | null;
}) {
  return (
    <Box component="span" id={id} sx={PROFILE_TOGGLE_TEXT_SX}>
      <Box component="span">{label}</Box>
      {isRequired && (
        <Box component="span" sx={{ color: "#ef4444", ml: 0.25 }}>
          *
        </Box>
      )}
      {helpText ? (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            verticalAlign: "middle",
            ml: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          <HelpTooltip text={helpText} />
        </Box>
      ) : null}
    </Box>
  );
}

function HelpTooltip({ text }: { text: string }) {
  const displayText = profileHelpTextForViewer(text);
  return (
    <Tooltip
      title={
        <Typography component="span" sx={{ whiteSpace: "pre-line", fontSize: "0.8125rem" }}>
          {displayText}
        </Typography>
      }
      placement="top"
      arrow
      slotProps={{
        tooltip: { sx: { maxWidth: 420 } },
      }}
    >
      <InfoOutlinedIcon
        data-testid="profile-help-tooltip-icon"
        sx={{ fontSize: 16, color: "#9ca3af", ml: 0.5, verticalAlign: "middle", cursor: "help" }}
      />
    </Tooltip>
  );
}

function NoteText({ text }: { text: string }) {
  return (
    <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mt: 0.5, fontFamily: "Rethink Sans, sans-serif" }}>
      {text}
    </Typography>
  );
}

function profileFieldInputIds(fieldKey: string): { inputId: string; labelId: string } {
  const safe = String(fieldKey).replace(/[^a-zA-Z0-9_-]/g, "_");
  return { inputId: `fp-ctrl-${safe}`, labelId: `fp-lbl-${safe}` };
}

/** Label sits above the control so long API strings never collide with placeholders/values. */
const STACKED_FIELD_LABEL_SX = {
  ...LABEL_SX,
  display: "block",
  fontWeight: 600,
  fontSize: "0.8125rem",
  lineHeight: 1.45,
  mb: 0.85,
  color: "#374151",
} as const;

function StackedFieldLabel({
  id,
  htmlFor,
  children,
  error,
}: {
  id: string;
  htmlFor: string;
  children: React.ReactNode;
  error?: boolean;
}) {
  return (
    <Typography
      id={id}
      component="label"
      htmlFor={htmlFor}
      variant="body2"
      sx={{
        ...STACKED_FIELD_LABEL_SX,
        ...(error ? { color: "#b91c1c" } : {}),
      }}
    >
      {children}
    </Typography>
  );
}

/** Sentinel: draft text is not a valid date — keep parent value on flush. */
export const PROFILE_DATE_DRAFT_UNCHANGED = Symbol("PROFILE_DATE_DRAFT_UNCHANGED");

/** Parse a date textbox draft into a profile API value (ISO date-only or null). */
export function resolveProfileDateDraftValue(draft: string): unknown | typeof PROFILE_DATE_DRAFT_UNCHANGED {
  const t = draft.trim();
  if (!t) {
    return null;
  }
  const iso = normalizeProfileDateInputToIso(t);
  return iso ?? PROFILE_DATE_DRAFT_UNCHANGED;
}

export type ProfileDateCommitApi = {
  mergePendingDateValues: (values: Record<string, unknown>) => Record<string, unknown>;
};

const ProfileDateCommitContext = createContext<{
  registerDraftGetter: (fieldKey: string, getDraft: () => string) => void;
  unregisterDraftGetter: (fieldKey: string) => void;
} | null>(null);

export function ProfileDateCommitProvider({
  children,
  apiRef,
}: {
  children: React.ReactNode;
  apiRef?: React.MutableRefObject<ProfileDateCommitApi | null>;
}) {
  const registryRef = useRef(new Map<string, () => string>());

  const registerDraftGetter = useCallback((fieldKey: string, getDraft: () => string) => {
    registryRef.current.set(fieldKey, getDraft);
  }, []);

  const unregisterDraftGetter = useCallback((fieldKey: string) => {
    registryRef.current.delete(fieldKey);
  }, []);

  const mergePendingDateValues = useCallback((values: Record<string, unknown>) => {
    const merged = { ...values };
    for (const [fieldKey, getDraft] of registryRef.current) {
      const resolved = resolveProfileDateDraftValue(getDraft());
      if (resolved !== PROFILE_DATE_DRAFT_UNCHANGED) {
        merged[fieldKey] = resolved;
      }
    }
    return merged;
  }, []);

  useEffect(() => {
    if (!apiRef) {
      return;
    }
    apiRef.current = { mergePendingDateValues };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, mergePendingDateValues]);

  return (
    <ProfileDateCommitContext.Provider value={{ registerDraftGetter, unregisterDraftGetter }}>
      {children}
    </ProfileDateCommitContext.Provider>
  );
}

/** Green "compliant – revalidation not due yet" badge shown when CMS confirms
 *  the provider is enrolled but has no revalidation due within ~6 months. */
type ProfileDateFieldProps = {
  fieldKey: string;
  inputId: string;
  labelId: string;
  labelNode: React.ReactNode;
  strVal: string;
  placeholder?: string | null;
  note_text?: string | null;
  disabled: boolean;
  hasError: boolean;
  cms_status?: string | null;
  onChange: (fieldKey: string, value: unknown) => void;
};

/**
 * Text-based date field so users can paste mm/dd/yyyy (or yyyy-mm-dd); values are
 * stored as ISO date-only strings for the profile API. Blur commits; invalid input reverts.
 */
function ProfileDateField({
  fieldKey,
  inputId,
  labelId,
  labelNode,
  strVal,
  placeholder,
  note_text,
  disabled,
  hasError,
  cms_status,
  onChange,
}: ProfileDateFieldProps) {
  const dateCommit = useContext(ProfileDateCommitContext);
  const nativeDateRef = useRef<HTMLInputElement>(null);
  const displayFromParent = () => (strVal ? isoDateOnlyToUsSlash(strVal) : "");
  const [draft, setDraft] = useState(displayFromParent);

  useEffect(() => {
    setDraft(displayFromParent());
  }, [strVal]);

  useEffect(() => {
    if (!dateCommit) {
      return;
    }
    dateCommit.registerDraftGetter(fieldKey, () => draft);
    return () => dateCommit.unregisterDraftGetter(fieldKey);
  }, [dateCommit, draft, fieldKey]);

  const nativePickerValue = useMemo(() => {
    const fromDraft = normalizeProfileDateInputToIso(draft);
    if (fromDraft) return fromDraft;
    const head = String(strVal ?? "").match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    if (head && parseIsoDateOnlyAsLocalDate(head)) return head;
    return "";
  }, [draft, strVal]);

  const commitDraft = () => {
    const resolved = resolveProfileDateDraftValue(draft);
    if (resolved === PROFILE_DATE_DRAFT_UNCHANGED) {
      setDraft(displayFromParent());
      return;
    }
    onChange(fieldKey, resolved);
    setDraft(resolved == null ? "" : isoDateOnlyToUsSlash(String(resolved)));
  };

  const openNativeDatePicker = () => {
    if (disabled) return;
    const el = nativeDateRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
        return;
      }
    } catch {
      // showPicker can throw when not user-gesture initiated in some browsers
    }
    el.click();
  };

  const handleNativePickerChange = (iso: string) => {
    if (!iso) return;
    onChange(fieldKey, iso);
    setDraft(isoDateOnlyToUsSlash(iso));
  };

  const isCompliantTbd = cms_status === "compliant_tbd" && !strVal;
  const isCertConflict = cms_status === "conflict";

  return (
    <Box sx={{ ...FIELD_BLOCK_SX, position: "relative" }}>
      <StackedFieldLabel id={labelId} htmlFor={inputId} error={hasError || isCertConflict}>
        {labelNode}
      </StackedFieldLabel>
      <TextField
        id={inputId}
        hiddenLabel
        size="medium"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={draft}
        placeholder={placeholder ?? "mm/dd/yyyy"}
        onChange={(e) => setDraft(formatUsDateDraftInput(e.target.value))}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          }
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData("text/plain");
          const iso = normalizeProfileDateInputToIso(text);
          e.preventDefault();
          if (iso) {
            onChange(fieldKey, iso);
            setDraft(isoDateOnlyToUsSlash(iso));
            return;
          }
          setDraft(formatUsDateDraftInput(text));
        }}
        disabled={disabled}
        fullWidth
        error={hasError}
        sx={OUTLINED_TEXTFIELD_SX}
        inputProps={{
          "aria-labelledby": labelId,
          maxLength: 10,
          sx: FIELD_SX,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                edge="end"
                disabled={disabled}
                aria-label="Open calendar"
                onClick={openNativeDatePicker}
                onMouseDown={(e) => e.preventDefault()}
                sx={{
                  color: "#0a829d",
                  "&:hover": {
                    color: "#086e86",
                    backgroundColor: "rgba(10, 130, 157, 0.08)",
                  },
                  "&.Mui-disabled": {
                    color: "#9ca3af",
                  },
                }}
              >
                <CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <input
        ref={nativeDateRef}
        type="date"
        value={nativePickerValue}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden
        onChange={(e) => handleNativePickerChange(e.target.value)}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
          border: 0,
          padding: 0,
          margin: 0,
        }}
      />
      {isCompliantTbd && <CompliantTbdBadge />}
      {isCertConflict && <CertConflictBadge />}
      {note_text && <NoteText text={note_text} />}
    </Box>
  );
}

function CertConflictBadge() {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.75,
        px: 1,
        py: 0.375,
        borderRadius: 1,
        bgcolor: "#fffbeb",
        border: "1px solid #fbbf24",
        color: "#92400e",
        fontFamily: "Rethink Sans, sans-serif",
        fontSize: "0.78rem",
        fontWeight: 500,
      }}
    >
      Saved date differs from CMS Provider of Services
    </Box>
  );
}

function CompliantTbdBadge() {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.75,
        px: 1,
        py: 0.375,
        borderRadius: 1,
        bgcolor: "#f0fdf4",
        border: "1px solid #86efac",
        color: "#15803d",
        fontFamily: "Rethink Sans, sans-serif",
        fontSize: "0.78rem",
        fontWeight: 500,
      }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "#16a34a" }} />
      Revalidation not due for 6+ months (confirmed by CMS)
    </Box>
  );
}

const ProfileField: React.FC<ProfileFieldProps> = ({ field, value, onChange, disabled = false, hasError = false }) => {
  const {
    field_key,
    label,
    field_type,
    hub_renderer_key,
    placeholder,
    help_text,
    note_text,
    is_required,
    options,
    cms_status,
  } = field;

  /** API-driven renderer id (falls back to field_type for backward compatibility). */
  const rawEffectiveType = hub_renderer_key || field_type;
  /** DMEPOS flag: checkbox + tooltip reads better than a switch for a regulatory election. */
  const effectiveType = field_key === "is_dmepos" ? "checkbox" : rawEffectiveType;

  const strVal = value !== null && value !== undefined ? String(value) : "";
  // Safely coerce array items to strings — stored accrediting-body values
  // come back from the API as {slug, name} objects when the field_type is
  // "multiselect" rather than "accrediting_bodies". Extract the slug (or key)
  // so the multiselect/checkbox_group renderers never receive a raw object.
  const arrVal: string[] = Array.isArray(value)
    ? (value as unknown[]).map((item) => {
        if (typeof item === "string") return item;
        if (item !== null && typeof item === "object") {
          const obj = item as Record<string, unknown>;
          if (typeof obj.slug === "string") return obj.slug;
          if (typeof obj.key === "string") return obj.key;
          if (typeof obj.value === "string") return obj.value;
        }
        return String(item ?? "");
      })
    : [];
  const boolVal = Boolean(value);

  const labelNode = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 0.25,
        rowGap: 0.25,
        maxWidth: "100%",
        whiteSpace: "normal",
        wordBreak: "break-word",
      }}
    >
      {label}
      {is_required && <Box component="span" sx={{ color: "#ef4444", ml: 0.25 }}>*</Box>}
      {help_text && <HelpTooltip text={help_text} />}
    </Box>
  );

  const { inputId, labelId } = profileFieldInputIds(field_key);

  const opts: ProfileFieldOption[] = options ?? [];

  // ---- Single-line text types ----
  if (effectiveType === "text" || effectiveType === "phone" || effectiveType === "email") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <StackedFieldLabel id={labelId} htmlFor={inputId} error={hasError}>
          {labelNode}
        </StackedFieldLabel>
        <TextField
          id={inputId}
          hiddenLabel
          size="medium"
          type={effectiveType === "email" ? "email" : "text"}
          value={strVal}
          placeholder={placeholder ?? undefined}
          onChange={(e) => onChange(field_key, e.target.value)}
          disabled={disabled}
          fullWidth
          error={hasError}
          helperText={hasError ? "Enter a valid value for this field" : undefined}
          sx={OUTLINED_TEXTFIELD_SX}
          inputProps={{ "aria-labelledby": labelId, "aria-invalid": hasError, sx: FIELD_SX }}
        />
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Multi-line textarea ----
  if (effectiveType === "textarea") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <StackedFieldLabel id={labelId} htmlFor={inputId}>
          {labelNode}
        </StackedFieldLabel>
        <TextField
          id={inputId}
          hiddenLabel
          size="medium"
          multiline
          minRows={3}
          value={strVal}
          placeholder={placeholder ?? undefined}
          onChange={(e) => onChange(field_key, e.target.value)}
          disabled={disabled}
          fullWidth
          sx={OUTLINED_TEXTFIELD_SX}
          inputProps={{ "aria-labelledby": labelId, sx: FIELD_SX }}
        />
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Date (text + paste/blur parse so mm/dd/yyyy and yyyy-mm-dd work; stored as ISO) ----
  if (effectiveType === "date") {
    return (
      <ProfileDateField
        fieldKey={field_key}
        inputId={inputId}
        labelId={labelId}
        labelNode={labelNode}
        strVal={strVal}
        placeholder={placeholder}
        note_text={note_text}
        disabled={disabled}
        hasError={hasError}
        cms_status={cms_status}
        onChange={onChange}
      />
    );
  }

  // ---- Number ----
  if (effectiveType === "number") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <StackedFieldLabel id={labelId} htmlFor={inputId}>
          {labelNode}
        </StackedFieldLabel>
        <TextField
          id={inputId}
          hiddenLabel
          size="medium"
          type="number"
          value={strVal}
          placeholder={placeholder ?? undefined}
          onChange={(e) => onChange(field_key, e.target.value === "" ? null : Number(e.target.value))}
          disabled={disabled}
          fullWidth
          sx={OUTLINED_TEXTFIELD_SX}
          inputProps={{ "aria-labelledby": labelId, sx: FIELD_SX }}
        />
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Year ----
  if (effectiveType === "year") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <StackedFieldLabel id={labelId} htmlFor={inputId}>
          {labelNode}
        </StackedFieldLabel>
        <TextField
          id={inputId}
          hiddenLabel
          size="medium"
          type="number"
          value={strVal}
          placeholder={placeholder ?? "YYYY"}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange(field_key, isNaN(n) ? null : n);
          }}
          disabled={disabled}
          fullWidth
          sx={OUTLINED_TEXTFIELD_SX}
          inputProps={{
            "aria-labelledby": labelId,
            min: 1900,
            max: 2100,
            step: 1,
            sx: FIELD_SX,
          }}
        />
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Currency ----
  if (effectiveType === "currency") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <StackedFieldLabel id={labelId} htmlFor={inputId}>
          {labelNode}
        </StackedFieldLabel>
        <TextField
          id={inputId}
          hiddenLabel
          size="medium"
          type="number"
          value={strVal}
          placeholder={placeholder ?? "0.00"}
          onChange={(e) => onChange(field_key, e.target.value)}
          disabled={disabled}
          fullWidth
          sx={OUTLINED_TEXTFIELD_SX}
          inputProps={{ "aria-labelledby": labelId, min: 0, step: "0.01", sx: FIELD_SX }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Select (single) ----
  if (effectiveType === "select") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <FormControl fullWidth disabled={disabled} error={hasError} margin="none" size="medium" variant="outlined">
          <StackedFieldLabel id={labelId} htmlFor={inputId} error={hasError}>
            {labelNode}
          </StackedFieldLabel>
          <Select
            id={inputId}
            labelId={labelId}
            size="medium"
            value={strVal}
            displayEmpty
            notched={false}
            onChange={(e: SelectChangeEvent) => onChange(field_key, e.target.value)}
            sx={SELECT_FIELD_SX}
            inputProps={{ "aria-labelledby": labelId }}
            MenuProps={{ PaperProps: { sx: SELECT_MENU_PAPER_SX } }}
          >
            <MenuItem value="" sx={MENU_ITEM_WRAP_SX}>
              <em style={{ color: "#9ca3af" }}>{placeholder ?? "Select…"}</em>
            </MenuItem>
            {opts.map((o) => (
              <MenuItem key={o.key} value={o.key} sx={MENU_ITEM_WRAP_SX}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          {hasError && (
            <FormHelperText>This field is required.</FormHelperText>
          )}
        </FormControl>
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Accrediting bodies picker (value is {slug, name}[]; API / legacy may send string slugs) ----
  if (effectiveType === "accrediting_bodies") {
    const bodyVal: { slug: string; name: string }[] = Array.isArray(value)
      ? (value as unknown[])
          .map((v) => {
            if (typeof v === "string") {
              const slug = v.trim();
              if (!slug) return null;
              const opt = opts.find((o) => o.key === slug);
              return { slug, name: opt?.label ?? slug };
            }
            if (v && typeof v === "object" && typeof (v as { slug?: unknown }).slug === "string") {
              const o = v as { slug: string; name?: string };
              const slug = o.slug.trim();
              if (!slug) return null;
              const opt = opts.find((x) => x.key === slug);
              const name =
                typeof o.name === "string" && o.name.trim() ? o.name.trim() : opt?.label ?? slug;
              return { slug, name };
            }
            return null;
          })
          .filter((row): row is { slug: string; name: string } => row != null)
      : [];
    const selectedSlugs = bodyVal.map((b) => b.slug);

    const handleChange = (e: SelectChangeEvent<string[]>) => {
      const selected = typeof e.target.value === "string"
        ? e.target.value.split(",")
        : e.target.value;
      const asObjects = selected.map((slug) => {
        const opt = opts.find((o) => o.key === slug);
        return { slug, name: opt?.label ?? slug };
      });
      onChange(field_key, asObjects);
    };

    return (
      <Box sx={FIELD_BLOCK_SX}>
        <FormControl fullWidth disabled={disabled} margin="none" size="medium" variant="outlined">
          <StackedFieldLabel id={labelId} htmlFor={inputId}>
            {labelNode}
          </StackedFieldLabel>
          <Select
            id={inputId}
            labelId={labelId}
            size="medium"
            multiple
            value={selectedSlugs}
            displayEmpty
            notched={false}
            onChange={handleChange}
            sx={SELECT_FIELD_SX}
            inputProps={{ "aria-labelledby": labelId }}
            MenuProps={{ PaperProps: { sx: SELECT_MENU_PAPER_SX } }}
            renderValue={(selected) => {
              if ((selected as string[]).length === 0) {
                return <em style={{ color: "#9ca3af" }}>{placeholder ?? "Select accrediting bodies…"}</em>;
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((slug) => {
                    const opt = opts.find((o) => o.key === slug);
                    return <Chip key={slug} label={opt?.label ?? slug} size="small" />;
                  })}
                </Box>
              );
            }}
          >
            {opts.length === 0 ? (
              <MenuItem disabled value="" sx={MENU_ITEM_WRAP_SX}>
                <em style={{ color: "#9ca3af" }}>No accrediting bodies available for this facility type</em>
              </MenuItem>
            ) : (
              opts.map((o) => (
                <MenuItem key={o.key} value={o.key} sx={MENU_ITEM_WRAP_SX}>
                  {o.label}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Multiselect ----
  if (effectiveType === "multiselect") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <FormControl fullWidth disabled={disabled} margin="none" size="medium" variant="outlined">
          <StackedFieldLabel id={labelId} htmlFor={inputId}>
            {labelNode}
          </StackedFieldLabel>
          <Select
            id={inputId}
            labelId={labelId}
            size="medium"
            multiple
            value={arrVal}
            displayEmpty
            notched={false}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const v = e.target.value;
              onChange(field_key, typeof v === "string" ? v.split(",") : v);
            }}
            inputProps={{ "aria-labelledby": labelId }}
            renderValue={(selected) => {
              if ((selected as string[]).length === 0) {
                return <em style={{ color: "#9ca3af" }}>{placeholder ?? "Select…"}</em>;
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((key) => {
                    const opt = opts.find((o) => o.key === key);
                    return <Chip key={key} label={opt?.label ?? key} size="small" />;
                  })}
                </Box>
              );
            }}
            sx={SELECT_FIELD_SX}
            MenuProps={{ PaperProps: { sx: SELECT_MENU_PAPER_SX } }}
          >
            {opts.map((o) => (
              <MenuItem key={o.key} value={o.key} sx={MENU_ITEM_WRAP_SX}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Radio ----
  if (effectiveType === "radio") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <FormControl disabled={disabled}>
          <FormLabel sx={{ ...STACKED_FIELD_LABEL_SX, mb: 0.75 }}>{labelNode}</FormLabel>
          <RadioGroup
            value={strVal}
            onChange={(e) => onChange(field_key, e.target.value)}
            row={opts.length <= 2}
          >
            {opts.map((o) => (
              <FormControlLabel
                key={o.key}
                value={o.key}
                control={<Radio size="small" />}
                sx={{ alignItems: "flex-start", ml: 0, mr: 1, mb: 0.5 }}
                label={<Typography variant="body2" sx={OPTION_LABEL_TYPOGRAPHY_SX}>{o.label}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Single checkbox (boolean) ----
  if (effectiveType === "checkbox") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <FormControlLabel
          control={
            <Checkbox
              id={inputId}
              checked={boolVal}
              onChange={(e) => onChange(field_key, e.target.checked)}
              disabled={disabled}
              size="small"
              inputProps={{ "aria-label": label }}
            />
          }
          label={<Typography variant="body2" sx={{ ...LABEL_SX }}>{labelNode}</Typography>}
        />
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Checkbox group (pick multiple from named list) ----
  if (effectiveType === "checkbox_group") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <FormControl component="fieldset" disabled={disabled}>
          <FormLabel component="legend" sx={{ ...STACKED_FIELD_LABEL_SX, mb: 0.75 }}>{labelNode}</FormLabel>
          <FormGroup>
            {opts.map((o) => (
              <FormControlLabel
                key={o.key}
                control={
                  <Checkbox
                    size="small"
                    checked={arrVal.includes(o.key)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...arrVal, o.key]
                        : arrVal.filter((k) => k !== o.key);
                      onChange(field_key, next);
                    }}
                  />
                }
                sx={{ alignItems: "flex-start", ml: 0, mr: 0, mb: 0.5 }}
                label={<Typography variant="body2" sx={OPTION_LABEL_TYPOGRAPHY_SX}>{o.label}</Typography>}
              />
            ))}
          </FormGroup>
        </FormControl>
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Toggle (boolean switch) ----
  if (effectiveType === "toggle") {
    return (
      <Box sx={FIELD_BLOCK_SX}>
        <Box sx={PROFILE_TOGGLE_ROW_SX}>
          <Switch
            id={inputId}
            checked={boolVal}
            onChange={(e) => onChange(field_key, e.target.checked)}
            disabled={disabled}
            sx={TEAL_SWITCH_SX}
            inputProps={{
              id: inputId,
              "aria-labelledby": labelId,
              "aria-label": label,
            }}
          />
          <ToggleInlineLabel
            id={labelId}
            label={label}
            isRequired={is_required}
            helpText={help_text}
          />
        </Box>
        {note_text && <NoteText text={note_text} />}
      </Box>
    );
  }

  // ---- Unknown type fallback ----
  console.warn(
    `[ProfileField] Unknown field_type "${effectiveType}" (stored: ${field_type}) for field "${field_key}" — rendering as text.`,
  );
  return (
    <Box sx={FIELD_BLOCK_SX}>
      <StackedFieldLabel id={labelId} htmlFor={inputId}>
        {labelNode}
      </StackedFieldLabel>
      <TextField
        id={inputId}
        hiddenLabel
        size="medium"
        value={strVal}
        placeholder={placeholder ?? undefined}
        onChange={(e) => onChange(field_key, e.target.value)}
        disabled={disabled}
        fullWidth
        sx={OUTLINED_TEXTFIELD_SX}
        inputProps={{ "aria-labelledby": labelId, sx: FIELD_SX }}
        helperText={`(Unknown field type: ${effectiveType})`}
      />
      {note_text && <NoteText text={note_text} />}
    </Box>
  );
};

export default ProfileField;
